import React, { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Row, Col, Button, Form as BsForm } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { listarGruposEmpresaConsultaCgcCpf } from '~/Services/Service';

const filtroSchema = Yup.object().shape({
  codigoGrupoEmpresa: Yup.number()
    .nullable()
    .when('nossoNumero', {
      is: (val) => val && val.length > 9,
      then: (schema) => schema.required('GE é obrigatória quando Nosso Número tem mais de 9 dígitos'),
    }),
  nossoNumero: Yup.string().nullable(),
  codigoTitulo: Yup.number().nullable().integer().positive(),
  valorInicial: Yup.number().nullable().min(0, 'Deve ser >= 0'),
  valorFinal: Yup.number()
    .nullable()
    .min(0, 'Deve ser >= 0')
    .when('valorInicial', (valorInicial, schema) =>
      valorInicial ? schema.min(valorInicial, 'Valor final deve ser ≥ valor inicial') : schema
    ),
  vencimentoInicial: Yup.date().nullable(),
  vencimentoFinal: Yup.date()
    .nullable()
    .when('vencimentoInicial', (vencimentoInicial, schema) =>
      vencimentoInicial ? schema.min(vencimentoInicial, 'Vencimento final deve ser ≥ inicial') : schema
    ),
  cgcCpf: Yup.string().nullable().max(20),
  bancoCheque: Yup.number().nullable().integer().positive(),
  agenciaCheque: Yup.number().nullable().integer().positive(),
  contaCheque: Yup.number().nullable().integer().positive(),
  numeroCheque: Yup.number().nullable().integer().positive(),
  numeroDuplicata: Yup.number().nullable().integer().positive(),
  contaCedente: Yup.number().nullable().integer().positive(),
  contaPortador: Yup.number().nullable().integer().positive(),
});

const initialValues = {
  codigoGrupoEmpresa: null,
  nossoNumero: '',
  codigoTitulo: '',
  bancoCheque: '',
  agenciaCheque: '',
  contaCheque: '',
  numeroCheque: '',
  valorInicial: '',
  valorFinal: '',
  vencimentoInicial: '',
  vencimentoFinal: '',
  contaCedente: '',
  contaPortador: '',
  cgcCpf: '',
  numeroDuplicata: '',
  tamanhoPagina: 20,
};

const FormFiltros = ({ onBuscar, onLimpar, pageSizes }) => {
  const [geOptions, setGeOptions] = useState([]);

  useEffect(() => {
    listarGruposEmpresaConsultaCgcCpf()
      .then((res) => {
        const lista = Array.isArray(res) ? res : [];
        setGeOptions(
          lista.map((g) => ({
            value: g.codigo_grupo_empresa,
            label: `${g.codigo_grupo_empresa} — ${g.apelido}`,
          }))
        );
      })
      .catch(() => toast.error('Erro ao carregar grupos de empresa.'));
  }, []);

  return (
    <Formik
    initialValues={initialValues}
    validationSchema={filtroSchema}
    onSubmit={(values) => {
      const filtros = {};
      Object.entries(values).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) filtros[k] = v;
      });
      onBuscar(filtros, 1);
    }}
  >
    {({ values, setFieldValue, resetForm, errors, touched }) => (
      <Form>
        <Row>
          <Col md={4} className="mb-2">
            <BsForm.Label className="small mb-1">Nosso Número</BsForm.Label>
            <Field name="nossoNumero" className="form-control" placeholder="Nosso Número" />
            <ErrorMessage name="nossoNumero" component="div" className="text-danger small" />
          </Col>
          <Col md={2} className="mb-2">
            <BsForm.Label className="small mb-1">Código do Título</BsForm.Label>
            <Field name="codigoTitulo" type="number" min="1" step="1" className="form-control" placeholder="Código" />
            <ErrorMessage name="codigoTitulo" component="div" className="text-danger small" />
          </Col>
          <Col md={3} className="mb-2">
            <BsForm.Label className="small mb-1">CGC/CPF</BsForm.Label>
            <Field name="cgcCpf" className="form-control" placeholder="CPF ou CNPJ" />
            <ErrorMessage name="cgcCpf" component="div" className="text-danger small" />
          </Col>
          <Col md={3} className="mb-2">
            <BsForm.Label className="small mb-1">GE (Grupo Empresa)</BsForm.Label>
            <Select
              isClearable
              options={geOptions}
              styles={customStyles}
              components={{ DropdownIndicator }}
              menuPortalTarget={document.body}
              placeholder="Selecione a GE..."
              onChange={(opt) => setFieldValue('codigoGrupoEmpresa', opt ? opt.value : null)}
              value={geOptions.find((o) => o.value === values.codigoGrupoEmpresa) || null}
            />
            {errors.codigoGrupoEmpresa && touched.codigoGrupoEmpresa && (
              <div className="text-danger small">{errors.codigoGrupoEmpresa}</div>
            )}
          </Col>
        </Row>

        <Row>
          <Col md={2} className="mb-2">
            <BsForm.Label className="small mb-1">Banco (Cheque)</BsForm.Label>
            <Field name="bancoCheque" type="number" min="1" step="1" className="form-control" placeholder="Banco" />
            <ErrorMessage name="bancoCheque" component="div" className="text-danger small" />
          </Col>
          <Col md={2} className="mb-2">
            <BsForm.Label className="small mb-1">Agência (Cheque)</BsForm.Label>
            <Field name="agenciaCheque" type="number" min="1" step="1" className="form-control" placeholder="Agência" />
            <ErrorMessage name="agenciaCheque" component="div" className="text-danger small" />
          </Col>
          <Col md={2} className="mb-2">
            <BsForm.Label className="small mb-1">Conta (Cheque)</BsForm.Label>
            <Field name="contaCheque" type="number" min="1" step="1" className="form-control" placeholder="Conta" />
            <ErrorMessage name="contaCheque" component="div" className="text-danger small" />
          </Col>
          <Col md={2} className="mb-2">
            <BsForm.Label className="small mb-1">Nº do Cheque</BsForm.Label>
            <Field name="numeroCheque" type="number" min="1" step="1" className="form-control" placeholder="Número" />
            <ErrorMessage name="numeroCheque" component="div" className="text-danger small" />
          </Col>
          <Col md={2} className="mb-2">
            <BsForm.Label className="small mb-1">Nº Duplicata</BsForm.Label>
            <Field name="numeroDuplicata" type="number" min="1" step="1" className="form-control" placeholder="Número" />
            <ErrorMessage name="numeroDuplicata" component="div" className="text-danger small" />
          </Col>
        </Row>

        <Row>
          <Col md={2} className="mb-2">
            <BsForm.Label className="small mb-1">Valor Inicial</BsForm.Label>
            <Field name="valorInicial" type="number" min="0" step="0.01" className="form-control" placeholder="0,00" />
            <ErrorMessage name="valorInicial" component="div" className="text-danger small" />
          </Col>
          <Col md={2} className="mb-2">
            <BsForm.Label className="small mb-1">Valor Final</BsForm.Label>
            <Field name="valorFinal" type="number" min="0" step="0.01" className="form-control" placeholder="0,00" />
            <ErrorMessage name="valorFinal" component="div" className="text-danger small" />
          </Col>
          <Col md={2} className="mb-2">
            <BsForm.Label className="small mb-1">Vencimento Inicial</BsForm.Label>
            <Field name="vencimentoInicial" type="date" className="form-control" />
            <ErrorMessage name="vencimentoInicial" component="div" className="text-danger small" />
          </Col>
          <Col md={2} className="mb-2">
            <BsForm.Label className="small mb-1">Vencimento Final</BsForm.Label>
            <Field name="vencimentoFinal" type="date" className="form-control" />
            <ErrorMessage name="vencimentoFinal" component="div" className="text-danger small" />
          </Col>
          <Col md={2} className="mb-2">
            <BsForm.Label className="small mb-1">Conta Cedente</BsForm.Label>
            <Field name="contaCedente" type="number" min="1" step="1" className="form-control" placeholder="Código" />
            <ErrorMessage name="contaCedente" component="div" className="text-danger small" />
          </Col>
          <Col md={2} className="mb-2">
            <BsForm.Label className="small mb-1">Conta Portador</BsForm.Label>
            <Field name="contaPortador" type="number" min="1" step="1" className="form-control" placeholder="Código" />
            <ErrorMessage name="contaPortador" component="div" className="text-danger small" />
          </Col>
        </Row>

        <Row className="mt-2">
          <Col>
            <Button variant="primary" type="submit" className="mr-2">
              Buscar
            </Button>
            <Button
              variant="outline-secondary"
              type="button"
              onClick={() => {
                resetForm();
                onLimpar();
              }}
            >
              Limpar
            </Button>
          </Col>
        </Row>
      </Form>
    )}
  </Formik>
  );
};

export default FormFiltros;
